import { useEffect, useRef, useState } from "react";
import { FANCY_STYLES, STYLE_CATEGORIES, applyStyle } from "@/lib/text-generators";

// Font-generator fonts grouped for the picker (every category except the
// "Symbols & Frames" decorations).
const GROUPS = STYLE_CATEGORIES.filter((c) => c.id !== "symbols")
  .map((c) => ({ category: c, styles: FANCY_STYLES.filter((s) => s.category === c.id) }))
  .filter((g) => g.styles.length > 0);

interface Props {
  value: string; // style id, "" = Normal
  onChange: (id: string) => void;
  /** Text previewed in each option (the user's caption). */
  sample: string;
}

/**
 * Caption font picker. Unlike a native <select> (plain text, OS-rendered, full
 * height), this renders each option as a LIVE PREVIEW of the caption in that font
 * and keeps the list compact (fixed height, scrolls).
 */
export function FontStylePicker({ value, onChange, sample }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const s = (sample.trim().slice(0, 14) || "Sample");
  const current = value ? FANCY_STYLES.find((x) => x.id === value) : null;
  const preview = (st: (typeof FANCY_STYLES)[number] | null) => (st ? applyStyle(st, s) : s);

  const Option = ({ st, selected }: { st: (typeof FANCY_STYLES)[number] | null; selected: boolean }) => (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={() => {
        onChange(st ? st.id : "");
        setOpen(false);
      }}
      className={
        "flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left transition-colors " +
        (selected ? "bg-primary/15 text-foreground" : "hover:bg-secondary text-foreground")
      }
    >
      <span className="min-w-0 flex-1 truncate text-base leading-snug">{preview(st)}</span>
      <span className="flex-none text-[10px] uppercase tracking-wider text-muted-foreground">{st ? st.name : "Normal"}</span>
    </button>
  );

  return (
    <div ref={ref} className="relative w-full max-w-xs">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-surface/60 px-2.5 py-1.5 text-sm transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="min-w-0 flex-1 truncate text-left">{current ? preview(current) : "Normal"}</span>
        <span aria-hidden="true" className="flex-none text-xs text-muted-foreground">{current ? current.name : ""} ▾</span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Caption font"
          className="absolute left-0 z-30 mt-1 max-h-72 w-72 max-w-[90vw] overflow-auto rounded-lg border border-border bg-background p-1 shadow-lg"
        >
          <Option st={null} selected={!value} />
          {GROUPS.map((g) => (
            <div key={g.category.id}>
              <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {g.category.name}
              </div>
              {g.styles.map((st) => (
                <Option key={st.id} st={st} selected={value === st.id} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
